import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { cvDryRun, evaluateCvDataset, addToDataset, getCandidatos, reescanearCandidato, updateCandidato, getListas, restoreDescartadosEnLista, getInterestWeights, updateInterestWeights, emitInterestWeightsUpdated, sendTestEmail, getUsers, updateUserPermissions, createUser, deleteUser, resetUser2fa } from "@/lib/api";
import { ArrowLeft, LayoutDashboard, FlaskConical, Palette, Loader2, FileText, Code, ListChecks, Check, X, Layers, RefreshCw, Pencil, Eraser, Settings, Sparkles, ArchiveRestore, SlidersHorizontal, FileQuestion, Users, ShieldCheck, ShieldOff, Mail, UserPlus, Trash2, EyeOff } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { AppMainHeader } from "@/components/AppMainHeader";
import { EditUserDialog } from "@/components/EditUserDialog";
import { toast } from "sonner";

const SECTIONS = [
  { id: "test-zone", label: "Test zone", icon: FlaskConical, description: "Área para pruebas y experimentos" },
  { id: "batch", label: "Batch", icon: Layers, description: "Acciones masivas sobre candidatos" },
  { id: "intereses", label: "Intereses", icon: SlidersHorizontal, description: "Pesos para el score según intereses de la agencia" },
  { id: "opciones", label: "Opciones", icon: Settings, description: "Preferencias de visualización" },
  { id: "colors", label: "Colors", icon: Palette, description: "Paleta de colores del sistema" },
  { id: "notificaciones", label: "Notificaciones", icon: Mail, description: "Correo de prueba para notificaciones de nuevo CV" },
  { id: "permisos", label: "Permisos", icon: Users, description: "Permisos de navegación por pestañas para cada usuario" },
];

const PERM_FIELDS = [
  { key: "candidatos", label: "Candidatos" },
  { key: "formularios", label: "Formularios" },
  { key: "revisiones", label: "Revisiones" },
  { key: "onboarding", label: "Onboarding" },
  { key: "proveedores", label: "Proveedores" },
  { key: "archivos", label: "Archivos" },
  { key: "metricas", label: "Métricas" },
  { key: "correos", label: "Correos" },
  { key: "gmb", label: "GMB" },
  { key: "material", label: "Material" },
  { key: "clientes", label: "Clientes" },
  { key: "chat", label: "Chat" },
  { key: "artefactos", label: "Artefactos" },
  { key: "admin", label: "Admin" },
];

function OpcionesSection() {
  const {
    showScore,
    showCursors,
    showIaSearch,
    setShowScore,
    setShowCursors,
    setShowIaSearch,
    demoHideUserNames,
    demoMaskEmail,
    demoMaskTelefono,
    demoMaskDireccion,
    demoMaskPortfolio,
    demoMaskNombre,
    demoHideCvPreview,
    setDemoHideUserNames,
    setDemoMaskEmail,
    setDemoMaskTelefono,
    setDemoMaskDireccion,
    setDemoMaskPortfolio,
    setDemoMaskNombre,
    setDemoHideCvPreview,
  } = useSettings();
  const [listas, setListas] = useState([]);
  const [listaRestaurarId, setListaRestaurarId] = useState("");
  const [isRestaurando, setIsRestaurando] = useState(false);

  useEffect(() => {
    getListas().then((data) => setListas(data.listas || [])).catch(() => setListas([]));
  }, []);

  const handleShowScoreChange = (checked) => setShowScore(checked);
  const handleShowCursorsChange = (checked) => setShowCursors(checked);
  const handleShowIaSearchChange = (checked) => setShowIaSearch(checked);

  const handleRestaurarDescartados = async () => {
    if (!listaRestaurarId) {
      toast.error("Elige una lista");
      return;
    }
    setIsRestaurando(true);
    try {
      const data = await restoreDescartadosEnLista(listaRestaurarId);
      const n = data.restaurados ?? 0;
      toast.success(n > 0 ? `${n} candidato${n !== 1 ? "s" : ""} restaurado${n !== 1 ? "s" : ""} a Por revisar` : "No había candidatos descartados en esta lista");
      if (n > 0) setListaRestaurarId("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al restaurar");
    } finally {
      setIsRestaurando(false);
    }
  };

  return (
    <section className="space-y-4 max-w-2xl">
      <p className="text-sm text-muted-foreground">
        Estas opciones son globales: si uno desactiva algo, se desactiva para todos los usuarios.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-lg border bg-card p-4">
          <p className="font-medium text-sm">Puntuación en tarjetas</p>
          <div className="flex items-center justify-between gap-3 mt-1">
            <Label htmlFor="opcion-show-score" className="text-sm text-muted-foreground cursor-pointer flex-1">
              Mostrar score en listados y detalle del candidato
            </Label>
            <Switch
              id="opcion-show-score"
              checked={showScore}
              onCheckedChange={handleShowScoreChange}
            />
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <p className="font-medium text-sm">Cursores en tiempo real</p>
          <div className="flex items-center justify-between gap-3 mt-1">
            <Label htmlFor="opcion-show-cursors" className="text-sm text-muted-foreground cursor-pointer flex-1">
              Ver tu cursor con avatar y el de otros usuarios
            </Label>
            <Switch
              id="opcion-show-cursors"
              checked={showCursors}
              onCheckedChange={handleShowCursorsChange}
            />
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4 sm:col-span-2">
          <p className="font-medium text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            Barra de búsqueda inteligente
          </p>
          <div className="flex items-center justify-between gap-3 mt-1">
            <Label htmlFor="opcion-show-ia-search" className="text-sm text-muted-foreground cursor-pointer flex-1">
              Mostrar la barra de búsqueda con IA en la página de candidatos
            </Label>
            <Switch
              id="opcion-show-ia-search"
              checked={showIaSearch}
              onCheckedChange={handleShowIaSearchChange}
            />
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4 sm:col-span-2">
          <p className="font-medium text-sm flex items-center gap-2">
            <EyeOff className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            Modo presentación (ocultar datos personales)
          </p>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            Para enseñar la app sin exponer datos reales. Los cambios se aplican en toda la app al instante.
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="demo-hide-names" className="text-sm cursor-pointer flex-1">
                Sustituir nombres de usuario por &quot;Usuario A&quot;, &quot;Usuario B&quot;, etc.
              </Label>
              <Switch
                id="demo-hide-names"
                checked={demoHideUserNames}
                onCheckedChange={setDemoHideUserNames}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Correo en candidatos</Label>
                <Select value={demoMaskEmail} onValueChange={setDemoMaskEmail}>
                  <SelectTrigger className="h-9 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Sin ocultar</SelectItem>
                    <SelectItem value="fake">Datos falsos</SelectItem>
                    <SelectItem value="blur">Emborronar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Teléfono</Label>
                <Select value={demoMaskTelefono} onValueChange={setDemoMaskTelefono}>
                  <SelectTrigger className="h-9 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Sin ocultar</SelectItem>
                    <SelectItem value="fake">Datos falsos</SelectItem>
                    <SelectItem value="blur">Emborronar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Dirección / ciudad</Label>
                <Select value={demoMaskDireccion} onValueChange={setDemoMaskDireccion}>
                  <SelectTrigger className="h-9 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Sin ocultar</SelectItem>
                    <SelectItem value="fake">Datos falsos</SelectItem>
                    <SelectItem value="blur">Emborronar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Portfolio / enlaces</Label>
                <Select value={demoMaskPortfolio} onValueChange={setDemoMaskPortfolio}>
                  <SelectTrigger className="h-9 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Sin ocultar</SelectItem>
                    <SelectItem value="fake">Datos falsos</SelectItem>
                    <SelectItem value="blur">Emborronar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Nombre del candidato</Label>
                <Select value={demoMaskNombre} onValueChange={setDemoMaskNombre}>
                  <SelectTrigger className="h-9 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Sin ocultar</SelectItem>
                    <SelectItem value="fake">Datos falsos</SelectItem>
                    <SelectItem value="blur">Emborronar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="demo-hide-cv" className="text-sm cursor-pointer flex-1">
                Ocultar vista previa del CV (mostrar placeholder en su lugar)
              </Label>
              <Switch
                id="demo-hide-cv"
                checked={demoHideCvPreview}
                onCheckedChange={setDemoHideCvPreview}
              />
            </div>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4 sm:col-span-2">
          <p className="font-medium text-sm flex items-center gap-2">
            <ArchiveRestore className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            Restaurar descartados
          </p>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            Pasa todos los candidatos descartados de una lista a &quot;Por revisar&quot;. Afecta a la lista para todos.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={listaRestaurarId} onValueChange={setListaRestaurarId}>
              <SelectTrigger className="w-full sm:w-[220px] h-10 sm:h-9">
                <SelectValue placeholder="Elige una lista" />
              </SelectTrigger>
              <SelectContent>
                {listas.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="secondary"
              size="sm"
              disabled={!listaRestaurarId || isRestaurando}
              onClick={handleRestaurarDescartados}
            >
              {isRestaurando ? <Loader2 className="w-4 h-4 animate-spin mr-2" strokeWidth={1.5} /> : null}
              Restaurar todos
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function TwoFaBadge({ enabled }) {
  return enabled ? (
    <Badge variant="outline" className="gap-1 border-emerald-300 text-emerald-700"><ShieldCheck className="w-3 h-3" /> 2FA</Badge>
  ) : (
    <Badge variant="outline" className="gap-1 text-muted-foreground"><ShieldOff className="w-3 h-3" /> Sin 2FA</Badge>
  );
}

function PermisosSection() {
  const { user } = useAuth();
  const { getDisplayName } = useSettings();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingEmail, setSavingEmail] = useState(null);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newNombre, setNewNombre] = useState("");
  const [adding, setAdding] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState(null);
  const [deletingEmail, setDeletingEmail] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [reset2faEmail, setReset2faEmail] = useState(null);
  const [resetting2fa, setResetting2fa] = useState(false);

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      const list = Array.isArray(data.users) ? data.users : [];
      setUsers(
        list.map((u) => ({
          ...u,
          permissions: u.permissions || {
            candidatos: true,
            formularios: true,
            revisiones: true,
            proveedores: true,
            archivos: true,
            metricas: true,
            correos: true,
            onboarding: true,
            gmb: true,
            material: true,
            clientes: true,
            chat: true,
            artefactos: true,
            admin: u.email === user?.email,
          },
        }))
      );
    } catch (_) {
      setUsers([]);
    }
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    getUsers()
      .then((data) => {
        if (!mounted) return;
        const list = Array.isArray(data.users) ? data.users : [];
        setUsers(
          list.map((u) => ({
            ...u,
            permissions: u.permissions || {
              candidatos: true,
              formularios: true,
              revisiones: true,
              proveedores: true,
              archivos: true,
              metricas: true,
              correos: true,
              onboarding: true,
              gmb: true,
              material: true,
              clientes: true,
              chat: true,
              artefactos: true,
              admin: u.email === user?.email,
            },
          }))
        );
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.response?.data?.detail || "Error al cargar usuarios");
        setUsers([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [user?.email]);

  const handleAddUser = async () => {
    const email = (newEmail || "").trim().toLowerCase();
    const password = (newPassword || "").trim();
    const nombre = (newNombre || "").trim();
    if (!email || !password) {
      toast.error("Email y contraseña son obligatorios");
      return;
    }
    setAdding(true);
    try {
      await createUser({ email, password, nombre: nombre || undefined });
      toast.success("Usuario creado");
      setAddOpen(false);
      setNewEmail("");
      setNewPassword("");
      setNewNombre("");
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear usuario");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteUser = async (emailToDelete) => {
    if (!emailToDelete) return;
    setDeletingEmail(emailToDelete);
    try {
      await deleteUser(emailToDelete);
      toast.success("Usuario eliminado");
      setDeleteConfirmEmail(null);
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    } finally {
      setDeletingEmail(null);
    }
  };

  const handleReset2fa = async (emailToReset) => {
    if (!emailToReset) return;
    setResetting2fa(true);
    try {
      await resetUser2fa(emailToReset);
      toast.success("2FA reseteado. Lo configurará en su próximo inicio de sesión");
      setReset2faEmail(null);
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al resetear el 2FA");
    } finally {
      setResetting2fa(false);
    }
  };

  const handleToggle = async (u, key, value) => {
    if (!u?.email) return;
    const email = u.email;
    setUsers((prev) =>
      prev.map((x) =>
        x.email === email ? { ...x, permissions: { ...(x.permissions || {}), [key]: value } } : x
      )
    );
    setSavingEmail(email);
    try {
      await updateUserPermissions(email, { [key]: value });
      toast.success("Permisos actualizados");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar permisos");
      // Revert on error by recargando lista
      try {
        const data = await getUsers();
        const list = Array.isArray(data.users) ? data.users : [];
        setUsers(
          list.map((u2) => ({
            ...u2,
            permissions: u2.permissions || {
              candidatos: true,
              formularios: true,
              revisiones: true,
              proveedores: true,
              archivos: true,
              metricas: true,
              correos: true,
              onboarding: true,
              gmb: true,
              material: true,
              clientes: true,
              chat: true,
              artefactos: true,
              admin: u2.email === user?.email,
            },
          }))
        );
      } catch (_) {}
    } finally {
      setSavingEmail(null);
    }
  };

  const isAdminPrincipal = (email) =>
    typeof process !== "undefined"
      ? false
      : false; // admin real se controla en backend; aquí solo deshabilitamos visualmente si coincide con user actual e is_admin

  return (
    <section className="space-y-4 max-w-3xl">
      <p className="text-sm text-muted-foreground">
        Configura qué secciones del header puede ver cada usuario. Solo el administrador principal puede cambiar estos
        permisos.
      </p>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" className="bg-white" onClick={() => setAddOpen(true)}>
          <UserPlus className="w-4 h-4 mr-2" />
          Añadir usuario
        </Button>
      </div>
      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
          {error}
        </div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
          Cargando usuarios…
        </div>
      ) : users.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay usuarios creados.</p>
      ) : (
        <>
        <ul className="md:hidden space-y-3" role="list">
          {users.map((uRow) => {
            const perms = uRow.permissions || {};
            const disabledAdminToggle = uRow.email === user?.email && user?.is_admin;
            const saving = savingEmail === uRow.email;
            const isCurrentUser = (uRow.email || "").toLowerCase() === (user?.email || "").toLowerCase();
            return (
              <li key={uRow.email} className="rounded-lg border bg-card">
                <div className="flex items-center gap-3 p-3 border-b">
                  <UserAvatar user={uRow} size={32} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{(getDisplayName(uRow) ?? uRow.nombre) || uRow.email}</p>
                    <p className="text-xs text-muted-foreground truncate">{uRow.email}</p>
                    <div className="mt-1"><TwoFaBadge enabled={uRow.totp_enabled} /></div>
                  </div>
                  {uRow.totp_enabled && (
                    <Button type="button" variant="ghost" size="icon" className="h-10 w-10 shrink-0" onClick={() => setReset2faEmail(uRow.email)} aria-label="Resetear 2FA">
                      <ShieldOff className="w-4 h-4" />
                    </Button>
                  )}
                  <Button type="button" variant="ghost" size="icon" className="h-10 w-10 shrink-0" onClick={() => setEditUser(uRow)} aria-label="Editar usuario">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {!isCurrentUser && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setDeleteConfirmEmail(uRow.email)}
                      disabled={deletingEmail === uRow.email}
                      aria-label="Eliminar usuario"
                    >
                      {deletingEmail === uRow.email ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-4 px-3 py-1">
                  {PERM_FIELDS.map(({ key, label }) => {
                    const isAdmin = key === "admin";
                    return (
                      <label key={key} className="flex min-h-11 items-center justify-between gap-2 text-sm">
                        <span className="truncate">{label}</span>
                        <Switch
                          checked={isAdmin ? perms.admin === true : perms[key] !== false}
                          onCheckedChange={(v) => handleToggle(uRow, key, v)}
                          disabled={saving || (isAdmin && disabledAdminToggle)}
                        />
                      </label>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="hidden md:block w-fit rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="px-4 py-2 text-left font-medium text-xs text-muted-foreground">Usuario</th>
                <th className="px-4 py-2 text-left font-medium text-xs text-muted-foreground">Email</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Candidatos</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Formularios</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Revisiones</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Onboarding</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Proveedores</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Archivos</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Métricas</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Correos</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">GMB</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Material</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Clientes</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Chat</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Artefactos</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">Admin</th>
                <th className="px-4 py-2 text-center font-medium text-xs text-muted-foreground">2FA</th>
                <th className="px-4 py-2 text-right font-medium text-xs text-muted-foreground w-20">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((uRow) => {
                const perms = uRow.permissions || {};
                const disabledAdminToggle = uRow.email === user?.email && user?.is_admin;
                const saving = savingEmail === uRow.email;
                const isCurrentUser = (uRow.email || "").toLowerCase() === (user?.email || "").toLowerCase();
                return (
                  <tr key={uRow.email} className="border-b last:border-b-0">
                    <td className="px-4 py-2 align-middle">
                      <div className="flex items-center gap-2">
                        <UserAvatar user={uRow} size={24} />
                        <span className="font-medium">{(getDisplayName(uRow) ?? uRow.nombre) || uRow.email}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 align-middle text-muted-foreground text-xs">{uRow.email}</td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.candidatos !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "candidatos", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.formularios !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "formularios", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.revisiones !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "revisiones", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.onboarding !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "onboarding", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.proveedores !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "proveedores", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.archivos !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "archivos", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.metricas !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "metricas", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.correos !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "correos", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.gmb !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "gmb", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.material !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "material", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.clientes !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "clientes", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.chat !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "chat", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.artefactos !== false}
                        onCheckedChange={(v) => handleToggle(uRow, "artefactos", v)}
                        disabled={saving}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <Switch
                        checked={perms.admin === true}
                        onCheckedChange={(v) => handleToggle(uRow, "admin", v)}
                        disabled={saving || disabledAdminToggle}
                      />
                    </td>
                    <td className="px-4 py-2 align-middle text-center">
                      <TwoFaBadge enabled={uRow.totp_enabled} />
                    </td>
                    <td className="px-4 py-2 align-middle text-right whitespace-nowrap">
                      {uRow.totp_enabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setReset2faEmail(uRow.email)}
                          title="Resetear 2FA (p. ej. móvil perdido)"
                        >
                          <ShieldOff className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setEditUser(uRow)}
                        title="Editar usuario"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {!isCurrentUser && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => setDeleteConfirmEmail(uRow.email)}
                          disabled={deletingEmail === uRow.email}
                        >
                          {deletingEmail === uRow.email ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Añadir usuario</DialogTitle>
            <DialogDescription>El nuevo usuario podrá acceder al panel con su email y contraseña.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="new-user-email">Email</Label>
              <Input
                id="new-user-email"
                type="email"
                placeholder="usuario@ejemplo.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-user-password">Contraseña</Label>
              <Input
                id="new-user-password"
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-user-nombre">Nombre (opcional)</Label>
              <Input
                id="new-user-nombre"
                type="text"
                placeholder="Nombre para mostrar"
                value={newNombre}
                onChange={(e) => setNewNombre(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={handleAddUser} disabled={adding}>
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Crear usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditUserDialog
        user={editUser}
        isAdminPrincipal={!!editUser && (editUser.email || "").toLowerCase() === (user?.email || "").toLowerCase()}
        onClose={() => setEditUser(null)}
        onSaved={loadUsers}
      />

      <Dialog open={!!reset2faEmail} onOpenChange={(open) => !open && setReset2faEmail(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Resetear 2FA</DialogTitle>
            <DialogDescription>
              Se borrará la verificación en dos pasos de <strong>{reset2faEmail}</strong> y se olvidarán sus dispositivos de confianza.
              En su próximo inicio de sesión tendrá que configurar Google Authenticator de nuevo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReset2faEmail(null)}>
              Cancelar
            </Button>
            <Button type="button" variant="destructive" onClick={() => handleReset2fa(reset2faEmail)} disabled={resetting2fa}>
              {resetting2fa ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Resetear 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirmEmail} onOpenChange={(open) => !open && setDeleteConfirmEmail(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar usuario</DialogTitle>
            <DialogDescription>
              ¿Eliminar a <strong>{deleteConfirmEmail}</strong>? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteConfirmEmail(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => deleteConfirmEmail && handleDeleteUser(deleteConfirmEmail)}
              disabled={deletingEmail === deleteConfirmEmail}
            >
              {deletingEmail === deleteConfirmEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/** Campos editables en batch: clave, etiqueta, cómo leer valor y cómo construir payload PATCH */
const BATCH_EDIT_FIELDS = [
  { key: "nombre", label: "Nombre", getValue: (c) => (c.nombre || "").toString(), buildPayload: (c, value, vaciar) => ({ nombre: vaciar ? "" : (value ?? "") }) },
  { key: "email", label: "Email", getValue: (c) => (c.email || "").toString(), buildPayload: (c, value, vaciar) => ({ email: vaciar ? "" : (value ?? "") }) },
  { key: "telefono", label: "Teléfono", getValue: (c) => (c.telefono || "").toString(), buildPayload: (c, value, vaciar) => ({ telefono: vaciar ? "" : (value ?? "") }) },
  { key: "ciudad", label: "Ciudad", getValue: (c) => (c.ciudad || "").toString(), buildPayload: (c, value, vaciar) => ({ ciudad: vaciar ? "" : (value ?? "") }) },
  { key: "provincia", label: "Provincia", getValue: (c) => (c.provincia || "").toString(), buildPayload: (c, value, vaciar) => ({ provincia: vaciar ? "" : (value ?? "") }) },
  { key: "nota", label: "Nota", getValue: (c) => (c.nota || "").toString(), buildPayload: (c, value, vaciar) => ({ nota: vaciar ? "" : (value ?? "") }) },
  { key: "rango_salarial", label: "Rango salarial", getValue: (c) => (c.rango_salarial || "").toString(), buildPayload: (c, value, vaciar) => ({ rango_salarial: vaciar ? "" : (value ?? "") }) },
  { key: "proyecto_destacado", label: "Proyecto destacado", getValue: (c) => (c.proyecto_destacado || "").toString(), buildPayload: (c, value, vaciar) => ({ proyecto_destacado: vaciar ? "" : (value ?? "") }) },
  { key: "situacion_actual", label: "Situación actual", getValue: (c) => (c.situacion_actual || "").toString(), buildPayload: (c, value, vaciar) => ({ situacion_actual: vaciar ? "" : (value ?? "") }) },
  { key: "rol_principal", label: "Rol principal", getValue: (c) => (c.rol_principal || "").toString(), buildPayload: (c, value, vaciar) => ({ rol_principal: vaciar ? "" : (value ?? "") }) },
  { key: "resumen_perfil", label: "Resumen perfil", getValue: (c) => (c.resumen_perfil || "").toString(), buildPayload: (c, value, vaciar) => ({ resumen_perfil: vaciar ? "" : (value ?? "") }) },
  { key: "nivel", label: "Nivel", getValue: (c) => (c.nivel || "").toString(), buildPayload: (c, value, vaciar) => ({ nivel: vaciar ? "" : (value ?? "") }) },
  { key: "portfolios_manuales.0", label: "Portfolio URL 1", getValue: (c) => ((c.portfolios_manuales && c.portfolios_manuales[0]) || "").toString(), buildPayload: (c, value, vaciar) => { const arr = (c.portfolios_manuales || []).slice(); if (vaciar) arr.splice(0, 1); else { arr[0] = value ?? ""; if (arr.length < 1) arr.length = 1; } return { portfolios_manuales: arr }; } },
  { key: "portfolios_manuales.1", label: "Portfolio URL 2", getValue: (c) => ((c.portfolios_manuales && c.portfolios_manuales[1]) || "").toString(), buildPayload: (c, value, vaciar) => { const arr = (c.portfolios_manuales || []).slice(); if (vaciar) arr.splice(1, 1); else { while (arr.length < 2) arr.push(""); arr[1] = value ?? ""; } return { portfolios_manuales: arr }; } },
  { key: "portfolios_manuales.2", label: "Portfolio URL 3", getValue: (c) => ((c.portfolios_manuales && c.portfolios_manuales[2]) || "").toString(), buildPayload: (c, value, vaciar) => { const arr = (c.portfolios_manuales || []).slice(); if (vaciar) arr.splice(2, 1); else { while (arr.length < 3) arr.push(""); arr[2] = value ?? ""; } return { portfolios_manuales: arr }; } },
  { key: "portfolios_manuales.3", label: "Portfolio URL 4", getValue: (c) => ((c.portfolios_manuales && c.portfolios_manuales[3]) || "").toString(), buildPayload: (c, value, vaciar) => { const arr = (c.portfolios_manuales || []).slice(); if (vaciar) arr.splice(3, 1); else { while (arr.length < 4) arr.push(""); arr[3] = value ?? ""; } return { portfolios_manuales: arr }; } },
  { key: "portfolios_manuales.4", label: "Portfolio URL 5", getValue: (c) => ((c.portfolios_manuales && c.portfolios_manuales[4]) || "").toString(), buildPayload: (c, value, vaciar) => { const arr = (c.portfolios_manuales || []).slice(); if (vaciar) arr.splice(4, 1); else { while (arr.length < 5) arr.push(""); arr[4] = value ?? ""; } return { portfolios_manuales: arr }; } },
];

const THEME_COLORS = [
  { name: "background", var: "--background", css: "hsl(var(--background))" },
  { name: "foreground", var: "--foreground", css: "hsl(var(--foreground))" },
  { name: "card", var: "--card", css: "hsl(var(--card))" },
  { name: "card-foreground", var: "--card-foreground", css: "hsl(var(--card-foreground))" },
  { name: "popover", var: "--popover", css: "hsl(var(--popover))" },
  { name: "popover-foreground", var: "--popover-foreground", css: "hsl(var(--popover-foreground))" },
  { name: "primary", var: "--primary", css: "hsl(var(--primary))" },
  { name: "primary-foreground", var: "--primary-foreground", css: "hsl(var(--primary-foreground))" },
  { name: "secondary", var: "--secondary", css: "hsl(var(--secondary))" },
  { name: "secondary-foreground", var: "--secondary-foreground", css: "hsl(var(--secondary-foreground))" },
  { name: "muted", var: "--muted", css: "hsl(var(--muted))" },
  { name: "muted-foreground", var: "--muted-foreground", css: "hsl(var(--muted-foreground))" },
  { name: "accent", var: "--accent", css: "hsl(var(--accent))" },
  { name: "accent-foreground", var: "--accent-foreground", css: "hsl(var(--accent-foreground))" },
  { name: "destructive", var: "--destructive", css: "hsl(var(--destructive))" },
  { name: "destructive-foreground", var: "--destructive-foreground", css: "hsl(var(--destructive-foreground))" },
  { name: "border", var: "--border", css: "hsl(var(--border))" },
  { name: "input", var: "--input", css: "hsl(var(--input))" },
  { name: "ring", var: "--ring", css: "hsl(var(--ring))" },
];

const getScoreColors = (score) => {
  if (score >= 75) return "bg-emerald-100 text-emerald-700";
  if (score >= 50) return "bg-amber-100 text-amber-700";
  if (score >= 25) return "bg-orange-100 text-orange-700";
  return "bg-red-100 text-red-700";
};

function TestZoneSection() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [cartaFile, setCartaFile] = useState(null);
  const [evalResult, setEvalResult] = useState(null);
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalError, setEvalError] = useState(null);
  const [datasetName, setDatasetName] = useState("");
  const [datasetExpected, setDatasetExpected] = useState("");
  const [datasetAddLoading, setDatasetAddLoading] = useState(false);
  const [datasetAddError, setDatasetAddError] = useState(null);
  const [datasetAddSuccess, setDatasetAddSuccess] = useState(false);

  useEffect(() => {
    if (result?.normalized) {
      const n = result.normalized;
      const expected = {
        rol_principal: n.rol_principal ?? "",
        segmentos: n.segmentos ?? [],
        nivel: n.nivel ?? "",
        años_experiencia: n.años_experiencia ?? 0,
      };
      setDatasetExpected(JSON.stringify(expected, null, 2));
      setDatasetAddSuccess(false);
      setDatasetAddError(null);
    }
  }, [result]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cvFile) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await cvDryRun(cvFile, cartaFile || null);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Error al analizar el CV");
    } finally {
      setLoading(false);
    }
  };

  const evalCardRef = useRef(null);
  const datasetFormRef = useRef(null);

  const handleAddToDataset = async () => {
    if (!datasetName.trim() || !cvFile || !datasetExpected.trim()) return;
    setDatasetAddLoading(true);
    setDatasetAddError(null);
    setDatasetAddSuccess(false);
    try {
      await addToDataset(datasetName.trim(), cvFile, datasetExpected.trim());
      setDatasetAddSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setDatasetAddError(typeof detail === "string" ? detail : err.message || "Error al añadir al dataset");
    } finally {
      setDatasetAddLoading(false);
    }
  };

  const handleEvaluateDataset = async () => {
    setEvalLoading(true);
    setEvalError(null);
    setEvalResult(null);
    try {
      const data = await evaluateCvDataset();
      if (data && typeof data.summary === "object" && Array.isArray(data.results)) {
        setEvalResult(data);
        evalCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        setEvalError("La respuesta del servidor no tiene el formato esperado.");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : err.message || "Error al evaluar el dataset";
      setEvalError(msg);
      evalCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } finally {
      setEvalLoading(false);
    }
  };

  return (
    <section
      id="test-zone"
      aria-label="Test zone"
      className="scroll-mt-6"
    >
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Análisis de CV (sin guardar)</CardTitle>
          <CardDescription>
            Sube un PDF para ejecutar el pipeline completo: extracción de texto, IA y normalización. No se crea ningún candidato en la base de datos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cv-file">CV (PDF) *</Label>
              <input
                id="cv-file"
                type="file"
                accept=".pdf"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
                onChange={(e) => setCvFile(e.target.files?.[0] || null)}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="carta-file">Carta de presentación (PDF, opcional)</Label>
              <input
                id="carta-file"
                type="file"
                accept=".pdf"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
                onChange={(e) => setCartaFile(e.target.files?.[0] || null)}
                disabled={loading}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={!cvFile || loading} aria-busy={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />
                    Analizando…
                  </>
                ) : (
                  "Analizar (sin guardar)"
                )}
              </Button>
              {result && cvFile && (
                <Button
                  type="button"
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => datasetFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  disabled={datasetAddLoading}
                >
                  {datasetAddLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />
                      Añadiendo…
                    </>
                  ) : (
                    "Añadir al dataset"
                  )}
                </Button>
              )}
            </div>
          </form>

          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
              {error}
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium">Score:</span>
                <span className={`text-sm font-semibold px-2.5 py-1 rounded ${getScoreColors(result.score_agencia ?? 0)}`}>
                  {result.score_agencia ?? 0}
                </span>
              </div>
              <Tabs defaultValue="texto" className="w-full">
                <TabsList className="flex flex-wrap h-auto gap-1">
                  <TabsTrigger value="texto" className="gap-1.5">
                    <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Texto extraído
                  </TabsTrigger>
                  <TabsTrigger value="enlaces">Enlaces embebidos</TabsTrigger>
                  <TabsTrigger value="raw" className="gap-1.5">
                    <Code className="w-3.5 h-3.5" strokeWidth={1.5} />
                    JSON IA
                  </TabsTrigger>
                  <TabsTrigger value="clasificacion" className="gap-1.5">
                    <ListChecks className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Clasificación final
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="texto" className="mt-3">
                  <div className="rounded-md border bg-muted/30 p-3 max-h-64 overflow-y-auto">
                    <p className="text-xs font-medium text-muted-foreground mb-2">CV</p>
                    <pre className="text-sm whitespace-pre-wrap break-words font-mono">{result.cv_text || "—"}</pre>
                    {result.carta_text && (
                      <>
                        <p className="text-xs font-medium text-muted-foreground mt-4 mb-2">Carta de presentación</p>
                        <pre className="text-sm whitespace-pre-wrap break-words font-mono">{result.carta_text}</pre>
                      </>
                    )}
                  </div>
                </TabsContent>
                <TabsContent value="enlaces" className="mt-3">
                  <div className="rounded-md border bg-muted/30 p-3 max-h-48 overflow-y-auto">
                    {result.embedded_links?.length ? (
                      <ul className="text-sm space-y-1 list-disc list-inside">
                        {result.embedded_links.map((url, i) => (
                          <li key={i}>
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                              {url}
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">Ninguno</p>
                    )}
                  </div>
                </TabsContent>
                <TabsContent value="raw" className="mt-3">
                  <div className="rounded-md border bg-muted/30 p-3 max-h-96 overflow-y-auto">
                    <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                      {JSON.stringify(result.raw_ai_json, null, 2)}
                    </pre>
                  </div>
                </TabsContent>
                <TabsContent value="clasificacion" className="mt-3">
                  <div className="rounded-md border bg-muted/30 p-4 space-y-4 max-h-96 overflow-y-auto">
                    {result.normalized && (
                      <>
                        {result.normalized.rol_principal && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Rol principal</p>
                            <p className="text-sm">{result.normalized.rol_principal}</p>
                          </div>
                        )}
                        {result.normalized.nivel && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Nivel</p>
                            <p className="text-sm">{result.normalized.nivel}</p>
                          </div>
                        )}
                        {(result.normalized.años_experiencia != null) && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Años experiencia</p>
                            <p className="text-sm">{result.normalized.años_experiencia}</p>
                          </div>
                        )}
                        {result.normalized.segmentos?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Segmentos</p>
                            <div className="flex flex-wrap gap-1.5">
                              {result.normalized.segmentos.map((s, i) => (
                                <Badge key={i} variant="secondary" className="text-xs font-normal">{s}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {result.normalized.habilidades?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Habilidades</p>
                            <p className="text-sm">{result.normalized.habilidades.join(", ")}</p>
                          </div>
                        )}
                        {result.normalized.experiencias_laborales?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Experiencia laboral</p>
                            <ul className="text-sm space-y-2">
                              {result.normalized.experiencias_laborales.slice(0, 8).map((exp, i) => (
                                <li key={i}>
                                  <span className="font-medium">{exp.cargo || "—"}</span>
                                  {exp.empresa && <span> en {exp.empresa}</span>}
                                  {exp.años_duracion != null && <span className="text-muted-foreground"> ({exp.años_duracion} años)</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {result.normalized.resumen_perfil && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Resumen perfil</p>
                            <p className="text-sm whitespace-pre-wrap">{result.normalized.resumen_perfil}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}

          {result && (
            <div ref={datasetFormRef} className="pt-6 border-t space-y-4">
              <p className="text-sm font-medium">Añadir al dataset de evaluación</p>
              <p className="text-xs text-muted-foreground">
                Guarda este CV y el resultado actual como referencia en <code className="bg-muted px-1 rounded">tests/cv_dataset/</code>. Después podrás ejecutar &quot;Evaluar dataset&quot; para medir precisión.
              </p>
              <div className="space-y-2">
                <Label htmlFor="dataset-name">Nombre del caso (ej. maria_cv)</Label>
                <input
                  id="dataset-name"
                  type="text"
                  placeholder="maria_cv"
                  value={datasetName}
                  onChange={(e) => setDatasetName(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                  disabled={datasetAddLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dataset-expected">JSON esperado (rol, segmentos, nivel, años_experiencia)</Label>
                <textarea
                  id="dataset-expected"
                  value={datasetExpected}
                  onChange={(e) => setDatasetExpected(e.target.value)}
                  rows={8}
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm font-mono"
                  disabled={datasetAddLoading}
                  spellCheck={false}
                />
              </div>
              <Button
                type="button"
                variant="destructive"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleAddToDataset}
                disabled={!datasetName.trim() || datasetAddLoading}
                aria-busy={datasetAddLoading}
              >
                {datasetAddLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />
                    Añadiendo…
                  </>
                ) : (
                  "Guardar en dataset"
                )}
              </Button>
              {datasetAddError && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
                  {datasetAddError}
                </div>
              )}
              {datasetAddSuccess && (
                <p className="text-sm text-emerald-600" role="status">
                  Añadido al dataset. Ya puedes ejecutar &quot;Evaluar dataset&quot; más abajo.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card ref={evalCardRef} className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">Evaluación del dataset de CVs</CardTitle>
          <CardDescription>
            Ejecuta el pipeline sobre los PDFs en <code className="text-xs bg-muted px-1 rounded">tests/cv_dataset/</code> y compara con los JSON esperados (<code className="text-xs bg-muted px-1 rounded">*_expected.json</code>). Mide precisión en rol, segmentos, nivel y años de experiencia.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleEvaluateDataset}
            disabled={evalLoading}
            aria-busy={evalLoading}
          >
            {evalLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />
                Evaluando…
              </>
            ) : (
              "Evaluar dataset"
            )}
          </Button>

          {evalError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
              {evalError}
            </div>
          )}

          {evalResult && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">Métricas globales</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-md border bg-muted/30 px-3 py-2">
                    <p className="text-xs text-muted-foreground">Rol accuracy</p>
                    <p className="text-lg font-semibold">{((evalResult.summary?.rol_accuracy ?? 0) * 100).toFixed(0)}%</p>
                  </div>
                  <div className="rounded-md border bg-muted/30 px-3 py-2">
                    <p className="text-xs text-muted-foreground">Segment accuracy</p>
                    <p className="text-lg font-semibold">{((evalResult.summary?.segment_accuracy ?? 0) * 100).toFixed(0)}%</p>
                  </div>
                  <div className="rounded-md border bg-muted/30 px-3 py-2">
                    <p className="text-xs text-muted-foreground">Nivel accuracy</p>
                    <p className="text-lg font-semibold">{((evalResult.summary?.nivel_accuracy ?? 0) * 100).toFixed(0)}%</p>
                  </div>
                  <div className="rounded-md border bg-muted/30 px-3 py-2">
                    <p className="text-xs text-muted-foreground">Experiencia accuracy</p>
                    <p className="text-lg font-semibold">{((evalResult.summary?.experiencia_accuracy ?? 0) * 100).toFixed(0)}%</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Total CVs evaluados: {evalResult.summary?.total_cvs ?? 0}</p>
                {(evalResult.summary?.total_cvs ?? 0) === 0 && (
                  <p className="text-sm text-muted-foreground mt-3 p-3 rounded-md bg-muted/50">
                    No hay PDFs en la carpeta del dataset. En el servidor, añade en <code className="bg-muted px-1 rounded">tests/cv_dataset/</code> archivos <code className="bg-muted px-1 rounded">nombre.pdf</code> y <code className="bg-muted px-1 rounded">nombre_expected.json</code> para que se ejecute la evaluación.
                  </p>
                )}
              </div>

              {evalResult.results?.length > 0 && (
                <div className="rounded-md border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left font-medium p-3">CV</th>
                          <th className="text-center font-medium p-3">Rol</th>
                          <th className="text-center font-medium p-3">Segmentos</th>
                          <th className="text-center font-medium p-3">Nivel</th>
                          <th className="text-center font-medium p-3">Experiencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evalResult.results.map((row, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="p-3 font-mono text-xs">{row.cv}</td>
                            <td className="p-3 text-center">
                              {row.error ? (
                                <span className="text-destructive text-xs">Error</span>
                              ) : row.rol_correct ? (
                                <Check className="w-4 h-4 text-emerald-600 inline" aria-label="Correcto" />
                              ) : (
                                <X className="w-4 h-4 text-destructive inline" aria-label="Incorrecto" />
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {row.error ? (
                                <span className="text-destructive text-xs">—</span>
                              ) : row.segmentos_correct ? (
                                <Check className="w-4 h-4 text-emerald-600 inline" aria-label="Correcto" />
                              ) : (
                                <X className="w-4 h-4 text-destructive inline" aria-label="Incorrecto" />
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {row.error ? (
                                <span className="text-destructive text-xs">—</span>
                              ) : row.nivel_correct ? (
                                <Check className="w-4 h-4 text-emerald-600 inline" aria-label="Correcto" />
                              ) : (
                                <X className="w-4 h-4 text-destructive inline" aria-label="Incorrecto" />
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {row.error ? (
                                <span className="text-destructive text-xs">—</span>
                              ) : row.experiencia_correct ? (
                                <Check className="w-4 h-4 text-emerald-600 inline" aria-label="Correcto" />
                              ) : (
                                <X className="w-4 h-4 text-destructive inline" aria-label="Incorrecto" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

const BATCH_REESCAN_STORAGE_KEY = "dashboard_batch_reescan_success";

function BatchSection() {
  const { maskValue } = useSettings();
  const [candidatos, setCandidatos] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [batchErrors, setBatchErrors] = useState([]);
  const [batchSuccessList, setBatchSuccessList] = useState(() => {
    try {
      const raw = sessionStorage.getItem(BATCH_REESCAN_STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr : [];
      }
    } catch (_) {}
    return [];
  });
  const [batchRescanDialogOpen, setBatchRescanDialogOpen] = useState(false);
  const [batchRescanPendingIds, setBatchRescanPendingIds] = useState([]);
  const [batchRescanPreviousIds, setBatchRescanPreviousIds] = useState(new Set());

  const [batchEditFieldKey, setBatchEditFieldKey] = useState(BATCH_EDIT_FIELDS[0]?.key ?? "");
  const [batchEditFilterContiene, setBatchEditFilterContiene] = useState("");
  const [batchEditValue, setBatchEditValue] = useState("");
  const [batchEditRunning, setBatchEditRunning] = useState(false);
  const [batchEditProgress, setBatchEditProgress] = useState({ current: 0, total: 0 });
  const [batchEditErrors, setBatchEditErrors] = useState([]);
  const [batchEditSuccess, setBatchEditSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    getCandidatos({})
      .then((data) => {
        if (!cancelled && data?.candidatos) setCandidatos(data.candidatos);
      })
      .catch((err) => {
        if (!cancelled) setListError(err.response?.data?.detail || err.message || "Error al cargar candidatos");
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const allIds = useMemo(() => candidatos.map((c) => c.id), [candidatos]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? new Set(allIds) : new Set());
  };

  const handleToggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (batchSuccessList.length > 0) {
      try {
        sessionStorage.setItem(BATCH_REESCAN_STORAGE_KEY, JSON.stringify(batchSuccessList));
      } catch (_) {}
    }
  }, [batchSuccessList]);

  const runReescanBatch = async (ids, clearSuccessList) => {
    if (ids.length === 0) {
      toast.info("No hay candidatos que reescanear (todos estaban ya reescaneados).");
      return;
    }
    if (clearSuccessList) {
      setBatchSuccessList([]);
      try {
        sessionStorage.removeItem(BATCH_REESCAN_STORAGE_KEY);
      } catch (_) {}
    }
    setBatchRunning(true);
    setBatchErrors([]);
    setBatchProgress({ current: 0, total: ids.length });
    const errors = [];
    for (let i = 0; i < ids.length; i++) {
      setBatchProgress((p) => ({ ...p, current: i + 1 }));
      const c = candidatos.find((x) => x.id === ids[i]);
      try {
        await reescanearCandidato(ids[i], c?.version ?? undefined);
        const nombre = c?.nombre || ids[i];
        setBatchSuccessList((prev) => {
          const next = [...prev, { id: ids[i], nombre }];
          try {
            sessionStorage.setItem(BATCH_REESCAN_STORAGE_KEY, JSON.stringify(next));
          } catch (_) {}
          return next;
        });
      } catch (err) {
        const name = c?.nombre || ids[i];
        errors.push({ id: ids[i], nombre: name, error: err.response?.data?.detail || err.message || "Error" });
      }
    }
    setBatchErrors(errors);
    setBatchRunning(false);
    setBatchProgress({ current: ids.length, total: ids.length });
  };

  const handleReescanearBatch = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const raw = sessionStorage.getItem(BATCH_REESCAN_STORAGE_KEY);
      const previous = raw ? (JSON.parse(raw) || []) : [];
      const previousIds = new Set(previous.map((x) => x.id));
      const overlap = ids.filter((id) => previousIds.has(id));
      if (overlap.length > 0) {
        setBatchRescanPendingIds(ids);
        setBatchRescanPreviousIds(previousIds);
        setBatchRescanDialogOpen(true);
        return;
      }
    } catch (_) {}
    runReescanBatch(ids, true);
  };

  const handleBatchRescanDialogAll = () => {
    setBatchRescanDialogOpen(false);
    const ids = [...batchRescanPendingIds];
    setBatchRescanPendingIds([]);
    setBatchRescanPreviousIds(new Set());
    runReescanBatch(ids, true);
  };

  const handleBatchRescanDialogRest = () => {
    setBatchRescanDialogOpen(false);
    const ids = batchRescanPendingIds.filter((id) => !batchRescanPreviousIds.has(id));
    setBatchRescanPendingIds([]);
    setBatchRescanPreviousIds(new Set());
    runReescanBatch(ids, false);
  };

  const batchEditField = useMemo(() => BATCH_EDIT_FIELDS.find((f) => f.key === batchEditFieldKey) || BATCH_EDIT_FIELDS[0], [batchEditFieldKey]);

  const handleFilterAndSelect = () => {
    if (!batchEditField) return;
    const needle = (batchEditFilterContiene || "").trim().toLowerCase();
    const matching = needle
      ? candidatos.filter((c) => batchEditField.getValue(c).toLowerCase().includes(needle))
      : candidatos;
    setSelectedIds(new Set(matching.map((c) => c.id)));
    setBatchEditSuccess(false);
    setBatchEditErrors([]);
  };

  const handleVaciarBatch = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !batchEditField) return;
    setBatchEditRunning(true);
    setBatchEditErrors([]);
    setBatchEditSuccess(false);
    setBatchEditProgress({ current: 0, total: ids.length });
    const errors = [];
    for (let i = 0; i < ids.length; i++) {
      setBatchEditProgress({ current: i + 1, total: ids.length });
      const c = candidatos.find((x) => x.id === ids[i]);
      if (!c) continue;
      try {
        const payload = batchEditField.buildPayload(c, null, true);
        if (c.version != null) payload.version = c.version;
        await updateCandidato(ids[i], payload);
      } catch (err) {
        errors.push({ id: ids[i], nombre: c.nombre || ids[i], error: err.response?.data?.detail || err.message || "Error" });
      }
    }
    setBatchEditErrors(errors);
    setBatchEditRunning(false);
    setBatchEditProgress({ current: ids.length, total: ids.length });
    setBatchEditSuccess(errors.length === 0);
    getCandidatos({}).then((data) => { if (data?.candidatos) setCandidatos(data.candidatos); }).catch(() => {});
  };

  const handleReemplazarBatch = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !batchEditField) return;
    setBatchEditRunning(true);
    setBatchEditErrors([]);
    setBatchEditSuccess(false);
    setBatchEditProgress({ current: 0, total: ids.length });
    const errors = [];
    const value = (batchEditValue ?? "").trim();
    for (let i = 0; i < ids.length; i++) {
      setBatchEditProgress({ current: i + 1, total: ids.length });
      const c = candidatos.find((x) => x.id === ids[i]);
      if (!c) continue;
      try {
        const payload = batchEditField.buildPayload(c, value, false);
        if (c.version != null) payload.version = c.version;
        await updateCandidato(ids[i], payload);
      } catch (err) {
        errors.push({ id: ids[i], nombre: c.nombre || ids[i], error: err.response?.data?.detail || err.message || "Error" });
      }
    }
    setBatchEditErrors(errors);
    setBatchEditRunning(false);
    setBatchEditProgress({ current: ids.length, total: ids.length });
    setBatchEditSuccess(errors.length === 0);
    getCandidatos({}).then((data) => { if (data?.candidatos) setCandidatos(data.candidatos); }).catch(() => {});
  };

  return (
    <section
      id="batch"
      aria-label="Batch"
      className="scroll-mt-6"
    >
      <p className="text-sm text-muted-foreground mb-6">
        Acciones masivas sobre candidatos. Selecciona uno o varios y ejecuta la acción deseada. Aquí se irán añadiendo más operaciones en el futuro.
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Columna: Reescaneo batch */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} aria-hidden />
              Reescaneo batch
            </CardTitle>
            <CardDescription>
              Vuelve a analizar el CV de los candidatos seleccionados (extracción, IA, normalización, segmentos y scoring). Solo aplicable a candidatos que tengan PDF subido.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {listError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
                {listError}
              </div>
            )}
            {listLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                Cargando candidatos…
              </div>
            ) : candidatos.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No hay candidatos para mostrar.</p>
            ) : (
              <>
                <div className="flex items-center gap-4 flex-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleSelectAll(!allSelected)}
                    disabled={batchRunning}
                  >
                    {allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {selectedIds.size} de {candidatos.length} seleccionados
                  </span>
                </div>
                <div className="rounded-md border overflow-hidden">
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left p-2 w-10">
                            <Checkbox
                              checked={allSelected}
                              onCheckedChange={handleSelectAll}
                              disabled={batchRunning}
                              aria-label={allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                            />
                          </th>
                          <th className="text-left font-medium p-2">Nombre</th>
                          <th className="text-left font-medium p-2 text-muted-foreground hidden sm:table-cell">Rol</th>
                        </tr>
                      </thead>
                      <tbody>
                        {candidatos.map((c) => (
                          <tr key={c.id} className="border-t border-border/50 hover:bg-muted/30">
                            <td className="p-2">
                              <Checkbox
                                checked={selectedIds.has(c.id)}
                                onCheckedChange={() => handleToggleOne(c.id)}
                                disabled={batchRunning}
                                aria-label={`Seleccionar ${maskValue(c.nombre ?? "", "nombre") || "candidato"}`}
                              />
                            </td>
                            <td className="p-2 font-medium">{maskValue(c.nombre ?? "", "nombre") || "—"}</td>
                            <td className="p-2 text-muted-foreground hidden sm:table-cell">{c.rol_principal || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <Button
                    type="button"
                    onClick={handleReescanearBatch}
                    disabled={!someSelected || batchRunning}
                    aria-busy={batchRunning}
                  >
                    {batchRunning ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />
                        Reescanando…
                      </>
                    ) : (
                      "Ejecutar reescaneo batch"
                    )}
                  </Button>
                  {batchRunning && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Progreso</span>
                        <span className="font-medium" aria-live="polite">
                          {batchProgress.current}/{batchProgress.total}
                        </span>
                      </div>
                      <Progress value={batchProgress.total ? (batchProgress.current / batchProgress.total) * 100 : 0} className="h-2" />
                    </div>
                  )}
                  {!batchRunning && batchErrors.length > 0 && (
                    <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
                      <p className="font-medium mb-1">Reescaneo completado con errores ({batchErrors.length})</p>
                      <ul className="list-disc list-inside text-xs space-y-0.5">
                        {batchErrors.slice(0, 5).map((e, i) => (
                          <li key={i}>{e.nombre}: {e.error}</li>
                        ))}
                        {batchErrors.length > 5 && <li>… y {batchErrors.length - 5} más</li>}
                      </ul>
                    </div>
                  )}
                  {!batchRunning && batchProgress.total > 0 && batchProgress.current === batchProgress.total && batchErrors.length === 0 && (
                    <p className="text-sm text-emerald-600 font-medium">Reescaneo completado correctamente.</p>
                  )}
                  {batchSuccessList.length > 0 && (
                    <div className="space-y-1.5 rounded-md border bg-muted/30 px-3 py-2">
                      <p className="text-xs font-medium text-muted-foreground">
                        Reescaneados correctamente ({batchSuccessList.length})
                      </p>
                      <ul className="max-h-32 overflow-y-auto list-disc list-inside text-xs text-muted-foreground space-y-0.5">
                        {batchSuccessList.map((item, i) => (
                          <li key={item.id}>{item.nombre}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <Dialog open={batchRescanDialogOpen} onOpenChange={(open) => {
                  if (!open) {
                    setBatchRescanPendingIds([]);
                    setBatchRescanPreviousIds(new Set());
                  }
                  setBatchRescanDialogOpen(open);
                }}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Ya reescaneados en la última ejecución</DialogTitle>
                      <DialogDescription>
                        Hay {batchRescanPendingIds.filter((id) => batchRescanPreviousIds.has(id)).length} candidato(s) que ya se reescanearon correctamente. ¿Quieres reescanear también esos o solo el resto?
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0">
                      <Button type="button" variant="outline" onClick={() => setBatchRescanDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button type="button" variant="secondary" onClick={handleBatchRescanDialogRest}>
                        Solo los que faltan
                      </Button>
                      <Button type="button" onClick={handleBatchRescanDialogAll}>
                        Reescanear todos
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </CardContent>
        </Card>

        {/* Columna: Edición batch por campo */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Pencil className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} aria-hidden />
              Edición batch por campo
            </CardTitle>
            <CardDescription>
              Elige un campo de la ficha del candidato, opcionalmente filtra por contenido (contiene), selecciona candidatos y vacía el campo o reemplázalo por un nuevo valor.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="batch-field">Campo</Label>
              <Select value={batchEditFieldKey} onValueChange={(v) => { setBatchEditFieldKey(v); setBatchEditSuccess(false); setBatchEditErrors([]); }}>
                <SelectTrigger id="batch-field" className="w-full">
                  <SelectValue placeholder="Seleccionar campo" />
                </SelectTrigger>
                <SelectContent>
                  {BATCH_EDIT_FIELDS.map((f) => (
                    <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="batch-filter">Filtrar por contenido</Label>
              <div className="flex gap-2">
                <Input
                  id="batch-filter"
                  placeholder='Contiene "xxxxxx"…'
                  value={batchEditFilterContiene}
                  onChange={(e) => setBatchEditFilterContiene(e.target.value)}
                  disabled={batchEditRunning || listLoading}
                  className="flex-1"
                />
                <Button type="button" variant="secondary" onClick={handleFilterAndSelect} disabled={batchEditRunning || listLoading}>
                  Seleccionar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Candidatos cuyo valor en este campo contiene el texto indicado pasarán a estar seleccionados en la lista de la izquierda.
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              <strong>{selectedIds.size}</strong> candidatos seleccionados (usa la tabla de la columna anterior o el filtro de arriba).
            </p>
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleVaciarBatch}
                disabled={!someSelected || batchEditRunning}
                aria-busy={batchEditRunning}
                className="gap-2"
              >
                <Eraser className="w-4 h-4" strokeWidth={1.5} aria-hidden />
                Vaciar campo
              </Button>
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="batch-new-value" className="text-xs">Reemplazar por</Label>
                  <Input
                    id="batch-new-value"
                    placeholder="Nuevo valor…"
                    value={batchEditValue}
                    onChange={(e) => setBatchEditValue(e.target.value)}
                    disabled={batchEditRunning}
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleReemplazarBatch}
                  disabled={!someSelected || batchEditRunning}
                  aria-busy={batchEditRunning}
                  className="gap-2 shrink-0"
                >
                  {batchEditRunning ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                      Actualizando…
                    </>
                  ) : (
                    "Reemplazar"
                  )}
                </Button>
              </div>
            </div>
            {batchEditRunning && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Progreso</span>
                  <span className="font-medium" aria-live="polite">
                    {batchEditProgress.current}/{batchEditProgress.total}
                  </span>
                </div>
                <Progress value={batchEditProgress.total ? (batchEditProgress.current / batchEditProgress.total) * 100 : 0} className="h-2" />
              </div>
            )}
            {!batchEditRunning && batchEditErrors.length > 0 && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
                <p className="font-medium mb-1">Edición completada con errores ({batchEditErrors.length})</p>
                <ul className="list-disc list-inside text-xs space-y-0.5">
                  {batchEditErrors.slice(0, 5).map((e, i) => (
                    <li key={i}>{e.nombre}: {e.error}</li>
                  ))}
                  {batchEditErrors.length > 5 && <li>… y {batchEditErrors.length - 5} más</li>}
                </ul>
              </div>
            )}
            {!batchEditRunning && batchEditSuccess && batchEditProgress.total > 0 && (
              <p className="text-sm text-emerald-600 font-medium">Campo actualizado correctamente en los candidatos seleccionados.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function InteresesSection() {
  const [weights, setWeights] = useState({});
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getInterestWeights()
      .then((data) => {
        if (!cancelled) {
          setWeights(data.weights || {});
          setSections(data.sections || []);
        }
      })
      .catch(() => {
        if (!cancelled) toast.error("Error al cargar los pesos de intereses");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateInterestWeights(weights);
      emitInterestWeightsUpdated();
      toast.success("Pesos guardados. Los scores se han actualizado en listados y fichas abiertas.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleSliderChange = (key, value) => {
    setWeights((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <section id="intereses" aria-label="Intereses de la agencia" className="scroll-mt-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
          Cargando…
        </div>
      </section>
    );
  }

  return (
    <section
      id="intereses"
      aria-label="Intereses de la agencia"
      className="scroll-mt-6"
    >
      <p className="text-sm text-muted-foreground mb-6">
        Ajusta del 0 al 10 cuánto quieres que cada factor sume (o reste, si es penalización) al score base. Los candidatos que cumplan cada criterio recibirán el bonus o penalización según el peso. Los cambios se guardan y se aplican en listados y ficha hasta que los modifiques.
      </p>
      <div className="space-y-8 max-w-2xl">
        {sections.map((section) => (
          <div key={section.name} className="rounded-lg border bg-card p-4 space-y-4">
            <h3 className="text-sm font-semibold text-foreground border-b pb-2">
              {section.name}
            </h3>
            <div className="space-y-4">
              {(section.items || []).map(({ key, label }) => (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between gap-4">
                    <Label htmlFor={`interest-${key}`} className="text-sm font-medium shrink-0">
                      {label}
                    </Label>
                    <span className="text-sm text-muted-foreground tabular-nums w-8 text-right">
                      {Math.round(Number(weights[key]) || 0)}
                    </span>
                  </div>
                  <Slider
                    id={`interest-${key}`}
                    min={0}
                    max={10}
                    step={1}
                    value={[Math.min(10, Math.max(0, Math.round(Number(weights[key]) || 0)))]}
                    onValueChange={([v]) => handleSliderChange(key, v)}
                    className="w-full"
                    aria-label={`${label}: ${Math.round(Number(weights[key]) || 0)} de 10`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          aria-busy={saving}
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />
              Guardando…
            </>
          ) : (
            "Guardar"
          )}
        </Button>
      </div>
    </section>
  );
}

function ColorsSection() {
  const {
    backgroundDotSize,
    backgroundGridSize,
    backgroundDotOpacity,
    backgroundDotColor,
    bodyBackgroundColor,
    setBackgroundDotSizeState,
    setBackgroundGridSizeState,
    setBackgroundDotOpacityState,
    setBackgroundDotColorState,
    setBodyBackgroundColorState,
    updateBackgroundSettings,
    loading,
    refetchSettings,
  } = useSettings();
  const [saving, setSaving] = useState(false);

  const handleSaveBackground = async () => {
    setSaving(true);
    try {
      await updateBackgroundSettings(
        Number(backgroundDotSize) || 2,
        Number(backgroundGridSize) || 24,
        Number(backgroundDotOpacity) ?? 0.12,
        backgroundDotColor || "#000000",
        bodyBackgroundColor || "#ffffff"
      );
      await refetchSettings();
      toast.success("Fondo guardado. Se aplica en Candidatos, Formularios y Dashboard.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      id="colors"
      aria-label="Colors"
      className="scroll-mt-6 space-y-8"
    >

      <div className="rounded-lg border border-gray-300 bg-card p-4 space-y-4 max-w-xl">
        <h3 className="font-medium text-sm">Fondo del body (trama de puntos)</h3>
        <p className="text-xs text-muted-foreground">
          Afecta a las rutas /candidatos, /formularios, /revisiones, /onboarding, /proveedores, /brutalfiles (Archivos), /correos, /métricas, /gmb, /material y /dashboard. Los cambios se aplican al guardar.
        </p>
        <div className="grid gap-4">
          <div>
            <Label className="text-xs">Tamaño de punto (px)</Label>
            <Slider
              value={[Number(backgroundDotSize) || 2]}
              onValueChange={([v]) => setBackgroundDotSizeState(v)}
              min={0.5}
              max={8}
              step={0.5}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-0.5">{Number(backgroundDotSize) || 2} px</p>
          </div>
          <div>
            <Label className="text-xs">Tamaño de trama (px)</Label>
            <Slider
              value={[Number(backgroundGridSize) || 24]}
              onValueChange={([v]) => setBackgroundGridSizeState(v)}
              min={8}
              max={80}
              step={2}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-0.5">{Number(backgroundGridSize) || 24} px</p>
          </div>
          <div>
            <Label className="text-xs">Opacidad de los puntos (0–1)</Label>
            <Slider
              value={[Number(backgroundDotOpacity) ?? 0.12]}
              onValueChange={([v]) => setBackgroundDotOpacityState(v)}
              min={0}
              max={1}
              step={0.05}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-0.5">{Number(backgroundDotOpacity) ?? 0.12}</p>
          </div>
          <div>
            <Label className="text-xs">Color de punto</Label>
            <div className="flex gap-2 items-center mt-1">
              <input
                type="color"
                value={backgroundDotColor || "#000000"}
                onChange={(e) => setBackgroundDotColorState(e.target.value)}
                className="h-10 w-14 rounded border border-input cursor-pointer"
              />
              <Input
                value={backgroundDotColor || "#000000"}
                onChange={(e) => setBackgroundDotColorState(e.target.value)}
                className="font-mono max-w-[140px]"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Color de fondo del body</Label>
            <div className="flex gap-2 items-center mt-1">
              <input
                type="color"
                value={bodyBackgroundColor || "#ffffff"}
                onChange={(e) => setBodyBackgroundColorState(e.target.value)}
                className="h-10 w-14 rounded border border-input cursor-pointer"
              />
              <Input
                value={bodyBackgroundColor || "#ffffff"}
                onChange={(e) => setBodyBackgroundColorState(e.target.value)}
                className="font-mono max-w-[140px]"
              />
            </div>
          </div>
          <Button onClick={handleSaveBackground} disabled={saving || loading} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : null}
            Guardar
          </Button>
        </div>
      </div>

      <div>
        <p className="text-sm text-muted-foreground mb-4">
          Variables CSS del tema (design tokens). Referencia rápida para mantener consistencia visual.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {THEME_COLORS.map(({ name, var: varName, css }) => (
            <Card key={name} className="overflow-hidden">
              <div
                className="h-20 w-full border-b"
                style={{ backgroundColor: `hsl(var(${varName}))` }}
                aria-hidden
              />
              <CardContent className="p-3">
                <p className="font-medium text-sm">{name}</p>
                <p className="text-xs text-muted-foreground font-mono truncate" title={varName}>
                  {varName}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function NotificacionesSection() {
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);

  const handleSendTest = async () => {
    const email = (testEmail || "").trim();
    if (!email) {
      toast.error("Indica un email de destino");
      return;
    }
    if (!email.includes("@")) {
      toast.error("Email no válido");
      return;
    }
    setSending(true);
    try {
      await sendTestEmail(email);
      toast.success("Correo de prueba enviado. Revisa la bandeja de entrada (y spam).");
      setTestEmail("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || err.message || "Error al enviar el correo");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="notificaciones"
      aria-label="Notificaciones"
      className="scroll-mt-6 space-y-8"
    >
      <div className="rounded-lg border border-gray-300 bg-card p-4 space-y-4 max-w-xl">
        <h3 className="font-medium text-sm">Correo de prueba</h3>
        <p className="text-xs text-muted-foreground">
          Envía un correo de prueba para comprobar que la configuración SMTP (notificaciones de nuevo CV) funciona correctamente.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px] space-y-1.5">
            <Label htmlFor="test-email-dest" className="text-xs">Destinatario</Label>
            <Input
              id="test-email-dest"
              type="email"
              placeholder="ej. trabajo@brutalco.es"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendTest()}
              className="max-w-sm"
              disabled={sending}
            />
          </div>
          <Button
            onClick={handleSendTest}
            disabled={sending || !(testEmail || "").trim()}
            className="gap-2"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <Mail className="w-4 h-4" strokeWidth={1.5} aria-hidden />}
            Enviar correo de prueba
          </Button>
        </div>
      </div>
    </section>
  );
}

function SectionContent({ sectionId }) {
  switch (sectionId) {
    case "test-zone":
      return <TestZoneSection />;
    case "batch":
      return <BatchSection />;
    case "intereses":
      return <InteresesSection />;
    case "opciones":
      return <OpcionesSection />;
    case "colors":
      return <ColorsSection />;
    case "notificaciones":
      return <NotificacionesSection />;
    case "permisos":
      return <PermisosSection />;
    default:
      return null;
  }
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authLoading } = useAuth();
  const hash = location.hash.slice(1) || null;
  const activeId = useMemo(() => {
    const found = SECTIONS.find((s) => s.id === hash);
    return found ? found.id : SECTIONS[0].id;
  }, [hash]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (!user.is_admin) {
      navigate("/candidatos", { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!hash && SECTIONS[0]) {
      navigate(`/dashboard#${SECTIONS[0].id}`, { replace: true });
    }
  }, [hash, navigate]);

  if (authLoading || !user || !user.is_admin) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  const handleSectionClick = (id) => {
    navigate(`/dashboard#${id}`);
  };

  const activeSection = SECTIONS.find((s) => s.id === activeId);

  return (
    <div className="min-h-dvh flex flex-col">
      <a
        href="#main-dashboard"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-30 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Saltar al contenido principal
      </a>

      <AppMainHeader
        title="Dashboard"
        titleSuffix={activeSection?.label ? ` / ${activeSection.label}` : ""}
        titleAs="h1"
      />

      <div className="flex flex-col md:flex-row flex-1 min-h-0">
        <nav
          aria-label="Secciones del dashboard"
          className="w-full md:w-52 shrink-0 border-b md:border-b-0 md:border-r border-gray-300 bg-background flex flex-row md:flex-col gap-1 md:gap-0 overflow-x-auto md:overflow-visible px-2 md:px-0 py-2 md:py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const isActive = activeId === section.id;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => handleSectionClick(section.id)}
                aria-current={isActive ? "true" : undefined}
                className={`flex shrink-0 items-center gap-2 px-3 md:px-4 py-2.5 md:mx-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap text-left ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden />
                {section.label}
              </button>
            );
          })}
        </nav>

        <main
          id="main-dashboard"
          role="main"
          className="flex-1 min-w-0 overflow-auto px-4 sm:px-6 py-6"
        >
          <SectionContent sectionId={activeId} />
        </main>
      </div>
    </div>
  );
}
